import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@foundit/ui-library";
import Keywords from "./components/keywords/Keywords";
import Location from "./components/location/Location";
import Tooltip from "./components/Tooltip";
import "./resources/scss/main.scss";
import Experience from "./components/experience/Experience";
import { IKeywords } from "./types/keywords";
import { IExperienceValues } from "./components/experience/ExperienceDropdown";
import { API_HEADERS, extractDomain, buildApiUrl } from "./utils/utils";
import { CATEGORIZE_KEYWORDS } from "./utils/endpoints";
import { ICategorizeKeywordsPayload, IHeaderSearchData } from "./types/base";
import CloseQSIcon from "./icons/CloseQSIcon";
import { TOOLTIP_ID } from "./components/keywords/Keywords";

interface IProps {
  middleware: string;
  userDetails: any;
  handleClose: () => void;
  handleEventListener?:({action, payload,page}:{action:string, payload?: any, page:string})=>void;
  page:string;
}

const QuickSearch: React.FC<IProps> = ({
  middleware = "",
  userDetails,
  handleClose,
  handleEventListener,
  page
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<IKeywords[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedExp, setSelectedExp] = useState<IExperienceValues>({
    min: "",
    max: "",
  });
  const [includeRelocating, setIncludingRelocating] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const isDropdownOpen = useRef<boolean>(false);
  const [activeField, setActiveField] = useState<'keywords' | 'location' | 'exp' | null>(null);

  const handleCloseWithAnimation = useCallback(() => {
    if(isDropdownOpen.current){
      isDropdownOpen.current = false;
      return;
    }

    setIsClosing(true);
    // Wait for slide up animation to complete before unmounting
    setTimeout(() => {
      handleClose();
    }, 400);
  }, [handleClose, isDropdownOpen]);

  const handleDropdownOpen = useCallback((open: boolean) => {
    isDropdownOpen.current = open;
  },[])

  const handleFieldActive = useCallback((field: 'keywords' | 'location' | 'exp' | null) => {
    setActiveField(field);
  }, [])

  useEffect(() => {
    if(typeof window === "undefined"){
      return;
    }
    // Trigger animation on mount using requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Add class to body to enable blur effect on db_mainLayout
    document.body.classList.add("qs_backdrop_active");

    // Handle ESC key press to close with animation
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseWithAnimation();
      }
    };

    window.addEventListener("keydown", handleEscKey);

    return () => {
      document.body.classList.remove("qs_backdrop_active");
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [handleCloseWithAnimation]);

  const isKeywordsError = useMemo(() => {
    return selectedKeywords?.map(item => item.text)?.join(" ")?.length > 250;
  }, [selectedKeywords]);

  const handleSearch = async () => {
    if(typeof window === "undefined" || isKeywordsError){
      return;
    }
    try {
      const searchData: IHeaderSearchData = {
        ...(selectedKeywords?.length > 0 && {
          keywords: selectedKeywords || [],
        }),
        ...(selectedLocations?.length > 0 && {
          locations: selectedLocations,
          includeRelocating,
        }),
        ...(!!(selectedExp?.max || selectedExp?.min) && {
          experience: {
            max_exp: selectedExp?.max?.split(" ")?.[0] || "",
            min_exp: selectedExp?.min?.split(" ")?.[0] || "",
          },
        }),
      };

      if(handleEventListener){
        handleEventListener({action:"search",payload:searchData,page })
      }

      const payload: ICategorizeKeywordsPayload = {
        keywords: selectedKeywords?.map((keyword) => keyword.text) || [],
      };

      const apiUrl = buildApiUrl(middleware, CATEGORIZE_KEYWORDS);
      if (!apiUrl) {
        return;
      }

      const response = await fetch(
        apiUrl,
        {
          method: "POST",
          headers: {
            ...API_HEADERS,
            credentials: "include",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData) {
        sessionStorage.setItem(
          "headerSearchData",
          JSON.stringify({
            searchData,
            categorizeKeywords: responseData,
            page
          })
        );

        window.location.href = `${extractDomain()}/edge/recruiter-search/search-results`;
      }
    } catch (error) {
      console.error("Error processing search:", error);
    }
  };
  const handleAdvanceSearch = () => {
    const searchData: IHeaderSearchData = {
      ...(selectedKeywords?.length > 0 && {
        keywords: selectedKeywords || [],
      }),
      ...(selectedLocations?.length > 0 && {
        locations: selectedLocations,
        includeRelocating,
      }),
      ...(!!(selectedExp?.max || selectedExp?.min) && {
        experience: {
          max_exp: selectedExp?.max?.split(" ")?.[0] || "",
          min_exp: selectedExp?.min?.split(" ")?.[0] || "",
        },
      }),
    };

    if(handleEventListener){
        handleEventListener({action:"advanceSearch",payload:searchData,page })
    }

    sessionStorage.setItem(
      "headerSearchData",
      JSON.stringify({
        searchData,
        page
      })
    );

    // redirect to search page
    window.location.href = `${extractDomain()}/edge/recruiter-search/search`;
  };

  // on Enter Click trigger search
  useEffect(() => {
    if(typeof window === "undefined"){
      return;
    }

    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (!activeField || activeField === "keywords") ){
        handleSearch();
      }
    };

    window.addEventListener("keydown", handleEnterKey);

    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [handleSearch, activeField]);

  return (
    <>
      {" "}
      <div
        className={`qs_wrapper ${
          isVisible && !isClosing ? "qs_wrapper_visible" : ""
        } ${isClosing ? "qs_wrapper_closing" : ""}`}
      >
        <div className="qs_section">
          <div className="qs_search_container">
            <div className="qs_title">Quick Search</div>
            <div className="qs_search">
              <div className="qs_search_inputs">
                {/* Keywords  */}
                <Keywords
                  selectedKeywords={selectedKeywords}
                  setSelectedKeywords={setSelectedKeywords}
                  middleware={middleware}
                  userDetails={userDetails}
                  isError={isKeywordsError}
                  handleDropdownOpen={handleDropdownOpen}
                  isActive={activeField === 'keywords'}
                  hasActiveField={activeField !== null}
                  handleFieldActive={handleFieldActive}
                />

                {/* Location  */}
                <Location
                  middleware={middleware}
                  userDetails={userDetails}
                  selectedLocations={selectedLocations}
                  setSelectedLocations={setSelectedLocations}
                  includeRelocating={includeRelocating}
                  setIncludingRelocating={setIncludingRelocating}
                  handleDropdownOpen={handleDropdownOpen}
                  isActive={activeField === 'location'}
                  hasActiveField={activeField !== null}
                  handleFieldActive={handleFieldActive}
                />

                {/* Experience  */}
                <Experience
                  setSelectedExp={setSelectedExp}
                  selectedExp={selectedExp}
                  handleDropdownOpen={handleDropdownOpen}
                  isActive={activeField === 'exp'}
                  hasActiveField={activeField !== null}
                  handleFieldActive={handleFieldActive}
                />
              </div>
              <Button
                type="contained_main"
                size="large"
                sx={{ width: "120px" }}
                onClick={handleSearch}
              >
                Search
              </Button>
            </div>
          </div>

          <div className="qs_or_section">OR</div>

          <div className="qs_advance_search">
            <div className="qs_advance_search_txt">
              Need more precise results? Use Advanced Search for detailed
              filters including skills, salary range, education, and more.
            </div>

            <Button type="text" size="medium" onClick={handleAdvanceSearch}>
              Advance Search
            </Button>
          </div>
        </div>
        <button
          className={`qs_drawer_close_btn ${isClosing ? "qs_drawer_close_btn_closing" : ""}`}
          onClick={handleCloseWithAnimation}
        >
          <div className="qs_drawer_close_icon">
            <CloseQSIcon />
          </div>
        </button>
      </div>
      <div 
        className={`qs_backdrop ${
          isVisible && !isClosing ? "qs_backdrop_visible" : ""
        } ${isClosing ? "qs_backdrop_closing" : ""}`}
        onClick={handleCloseWithAnimation} 
      />
      <Tooltip id={TOOLTIP_ID} place="top-start" delayHide={0} watch={selectedKeywords} />
    </>
  );
};

export default QuickSearch;
